import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A custom element that renders a zoom dialog with swipe + keyboard navigation.
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = new Set();
  #boundHandleKeyDown = this.handleKeyDown.bind(this);

  // --- Autoplay media (video / external iframe) in the active zoom slide ---
  #autoplayVideoIn(container) {
    if (!container) return;

    // 1) Activate deferred-media (Shopify often gates video/iframe behind it)
    const deferred = container.querySelector('deferred-media');
    if (deferred) {
      // Try the public method (varies by theme)
      deferred.loadContent?.();

      // Fallback: some themes only load when the poster button is clicked
      const posterButton = deferred.querySelector(
        'button, [type="button"], .deferred-media__poster'
      );
      posterButton?.click?.();
    }

    // 2) Wait a tick for the video/iframe to actually be inserted into the DOM
    setTimeout(() => {
      // Native <video>
      const video = container.querySelector('video');
      if (video) {
        video.muted = true;        // autoplay requires muted in most browsers
        video.playsInline = true;  // iOS
        video.controls = true;     // optional
        video.play?.().catch(() => {});
        return;
      }

      // External video <iframe> (YouTube/Vimeo)
      const iframe = container.querySelector('iframe');
      if (iframe?.src) {
        try {
          const url = new URL(iframe.src, window.location.href);
          url.searchParams.set('autoplay', '1');
          url.searchParams.set('muted', '1');

          // YouTube sometimes needs this to allow JS api/autoplay behavior
          if (!url.searchParams.has('enablejsapi')) {
            url.searchParams.set('enablejsapi', '1');
          }

          // Only update if changed (prevents reload loops)
          const next = url.toString();
          if (iframe.src !== next) iframe.src = next;
        } catch (_) {}
      }
    }, 50);
  }
  
  // swipe state
  #touchStartX = 0;
  #touchEndX = 0;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.refs.dialog.addEventListener('keydown', this.#boundHandleKeyDown);

    // swipe gestures
    this.refs.dialog.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches.length === 1) {
          this.#touchStartX = e.touches[0].screenX;
        }
      },
      { passive: true }
    );

    this.refs.dialog.addEventListener(
      'touchend',
      (e) => {
        if (e.changedTouches.length === 1) {
          this.#touchEndX = e.changedTouches[0].screenX;
          this.#handleSwipe();
        }
      },
      { passive: true }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.refs.dialog.removeEventListener('keydown', this.#boundHandleKeyDown);
  }

  #handleSwipe() {
    const swipeDistance = this.#touchEndX - this.#touchStartX;
    const minSwipe = 50;
    if (Math.abs(swipeDistance) < minSwipe) return;

    if (swipeDistance > 0) {
      this.prev();
    } else {
      this.next();
    }
  }

  async open(index, event) {
    event?.preventDefault?.();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

      const open = () => {
      dialog.showModal();
      for (const target of [targetThumbnail, targetImage]) {
      target?.scrollIntoView({ behavior: 'instant' });
      }

      // autoplay the media in the opened slide
      requestAnimationFrame(() => this.#autoplayVideoIn(targetImage));
    };


    const sourceImage =
      event?.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions || !sourceImage || !targetImage) return open();

    const transitionName = `gallery-item`;
    sourceImage.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', transitionName);
    });

    targetImage.style.removeProperty('view-transition-name');
    this.selectThumbnail(index, { behavior: 'instant' });
  }

  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;
    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.#loadHighResolutionImage(mostVisibleElement);
    // ✅ NEW: when user swipes/scrolls to a video slide, load + autoplay it
    requestAnimationFrame(() => this.#autoplayVideoIn(mostVisibleElement));
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  async close() {
    const { dialog, media } = this.refs;
    if (!supportsViewTransitions) return this.closeDialog();

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const transitionName = `gallery-item`;

    const mediaGallery = this.closest('media-gallery');
    const slideshowActive = mediaGallery?.presentation === 'carousel';
    const slide = slideshowActive
      ? mediaGallery.slideshow?.slides?.[activeIndex]
      : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();

    dialog.classList.add('dialog--closed');
    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  async next() {
    const { media } = this.refs;
    if (!media || media.length <= 1) return;

    const currentElement = await getMostVisibleElement(media);
    const currentIndex = media.indexOf(currentElement);
    const nextIndex = (currentIndex + 1) % media.length;
    this.selectThumbnail(nextIndex, { behavior: 'instant' });
  }

  prev() {
    const { media } = this.refs;
    if (!media || media.length <= 1) return;
    getMostVisibleElement(media).then((el) => {
      const i = media.indexOf(el);
      const prevIndex = (i - 1 + media.length) % media.length;
      this.selectThumbnail(prevIndex, { behavior: 'instant' });
    });
  }

  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      return this.close();
    }
    if (event.key === 'ArrowRight' || event.key === ' ') {
      event.preventDefault();
      return this.next();
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      return this.prev();
    }
  }

  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;
    this.#loadHighResolutionImage(media[index]);
  }

  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];
    if (!targetThumbnail) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

        const targetImage = media[index];
    if (targetImage) {
      targetImage.scrollIntoView({ behavior: options.behavior });
      this.#loadHighResolutionImage(targetImage);

      // ✅ autoplay the media in the newly selected slide
      requestAnimationFrame(() => this.#autoplayVideoIn(targetImage));
    }

    this.dispatchEvent(new ZoomMediaSelectedEvent(index));

  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(mostVisible.target);
      },
      { threshold: Array.from({ length: 100 }, (_, i) => i / 100) }
    );
    for (const element of elements) {
      observer.observe(element);
    }
  });
}
